import { forwardRef, InputGroup, InputLeftElement, PopoverTrigger, Spacer, useFormControl, useToken } from '@chakra-ui/react';
import { format } from 'date-fns';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@bonusly/ds-button';
import { rem } from '@bonusly/ds-utils';

import { useMonthPickerContext } from '../month-picker-context';

type MonthRangeInputProps = {
	leftIcon?: ReactElement;
};

export const MonthRangeInput = forwardRef<MonthRangeInputProps, 'button'>(({ leftIcon, ...rest }, ref) => {
	const errorColor = useToken('colors', 'error.inputBorder');
	const formProps = useFormControl({ readOnly: true });
	const { t } = useTranslation('components');
	const { displayFormat, isDisabled, onOpen, value } = useMonthPickerContext();

	const formatMonth = (date: Date | null, formatType: 'short' | 'long' = 'short') => {
		if (!date) return '';
		return formatType === 'short' ? format(date, 'MMM yyyy') : format(date, 'MMMM yyyy');
	};

	const [start, end] = value;
	const isEmpty = !start && !end;

	let displayValue = t('date_picker.select_month_range');

	if (start && end) {
		const startFormatted = formatMonth(start, displayFormat);
		const endFormatted = formatMonth(end, displayFormat);

		// If start and end are the same month and year, show just one month
		if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
			displayValue = startFormatted;
		} else {
			displayValue = `${startFormatted} - ${endFormatted}`;
		}
	} else if (start) {
		displayValue = formatMonth(start, displayFormat);
	}

	return (
		<PopoverTrigger>
			<InputGroup>
				{leftIcon && (
					<InputLeftElement pointerEvents="none" color={isDisabled ? 'text.disabled' : 'text.default'}>
						{leftIcon}
					</InputLeftElement>
				)}
				<Button
					bg={isDisabled ? 'secondary.bgAlpha' : 'white'}
					borderColor="gray.200"
					borderStyle="solid"
					borderWidth="1px"
					color={isDisabled ? 'text.disabled' : !isEmpty ? 'text.default' : 'text.softest'}
					cursor={isDisabled ? 'not-allowed' : 'pointer'}
					justifyContent="flex-start"
					fontWeight="normal"
					minH={rem(40)}
					minW={rem(192)}
					onClick={isDisabled ? undefined : onOpen}
					ref={ref}
					px={3}
					py={2}
					_active={
						isDisabled
							? {
									borderColor: 'gray.200',
									outline: 'none',
									outlineOffset: '0',
								}
							: {
									borderColor: 'border.focus',
									outline: '1px solid',
									outlineOffset: '0',
								}
					}
					_hover={{
						bg: isDisabled ? 'secondary.bgAlpha' : 'white',
					}}
					_invalid={{
						borderColor: 'error.inputBorder',
						boxShadow: `0 0 0 1px ${errorColor}`,
					}}
					{...(leftIcon && { pl: 10 })}
					{...formProps}
					{...rest}
				>
					{displayValue}
					<Spacer />
				</Button>
			</InputGroup>
		</PopoverTrigger>
	);
});

MonthRangeInput.displayName = 'MonthRangeInput';
